import React, { useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  Box,
  Typography,
  FormControl,
  Radio,
  FormControlLabel,
  Button
} from "@mui/material";
import { UploadButton } from "../Buttons";
import { Tooltip } from "../Tooltip";
import { PlayCircleFilled } from "@mui/icons-material";
import {
  TextField,
  fieldToTextField,
  RadioGroup,
  CheckboxWithLabel
} from "formik-mui";
import { Field, ErrorMessage, useFormikContext } from "formik";
import MuiTextField from "@mui/material/TextField";
import { useTranslation } from "react-i18next";
import {
  repositoryFileMimeTypes,
  repositoryItemDurationOptions,
  activityTypes,
  audioMimeType,
  htmlMimeType,
  imageFileMimeTypes,
  HTML5SupportedVideoMimeTypes,
  editorInlineItemsBehavior
} from "../../constants";
import Select from "../FormikFields/Select";
import NewPageField from "./NewPageField";
import ItemPreviewModal from "../../views/Content/Repository/ManageRepository/ItemPreviewModal";
import HTMLEditor from "../HtmlEditor/HtmlEditor";
import ChipInput from "../FormikFields/ChipInput";

export const newContentFormFields = {
  file: "file",
  link: "url",
  name: "name",
  content: "content",
  description: "description",
  tags: "tags",
  comment: "comment",
  durationHours: "durationHours",
  durationMinutes: "durationMinutes",
  courseTitle: "courseTitle",
  isQuizImport: "isQuizImport",
  quizOrSurvey: "quizType",
  quizInstructions: "instructions",
  randomlyOrdered: "randomlyOrdered",
  numQuestions: "numQuestions",
  anonymousReporting: "anonymousReporting",
  questionsPerPage: "questionsPerPage",
  editorBehavior: "editorBehavior"
};

const useStyles = makeStyles(theme => ({
  chooseFilesField: {
    textOverflow: "ellipsis"
  },
  numQuestionsField: {
    width: 96
  },
  errorMsg: {
    color: theme.palette.error.main,
    fontSize: "0.75rem"
  }
}));

const durationMenuItems = repositoryItemDurationOptions.map(option => ({
  value: option,
  label: option
}));

const getFileType = file => {
  let type = null;
  if (file) {
    if (file.type === audioMimeType) {
      type = activityTypes.audio;
    } else if (file.type === htmlMimeType) {
      type = activityTypes.page;
    } else if (imageFileMimeTypes.includes(file.type)) {
      type = activityTypes.image;
    } else if (HTML5SupportedVideoMimeTypes.includes(file.type)) {
      type = activityTypes.video;
    }
  }
  return type;
};

export const ChooseFileField = ({ multiple, linkedField }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [fileName, setFileName] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);

  const handleFileChange = file => {
    let name = "";
    if (file && file.length > 0) {
      name = `${file[0].name} ${
        file.length > 1 ? ` + ${file.length - 1} more` : ""
      }`;
    }
    setFileName(name);
  };

  const previewableFileTypes = [
    activityTypes.video,
    activityTypes.audio,
    activityTypes.pdf,
    activityTypes.page,
    activityTypes.image
  ];

  const previewButtonTooltipMessage = file => {
    if (file) {
      if (getFileType(file)) {
        return t("addNewContent.addItem.Preview");
      } else {
        return t("addNewContent.addItem.filePreviewErrorMsg");
      }
    } else {
      return t("addNewContent.addItem.chooseFiles");
    }
  };

  const previewMsg = file => {
    return (
      <Typography
        variant="body2"
        color="textPrimary"
        data-qa="preview-button-tooltip-text"
      >
        {previewButtonTooltipMessage(file)}
      </Typography>
    );
  };

  return (
    <Field
      label={t("addNewContent.addItem.file")}
      name={newContentFormFields.file}
      variant="filled"
      fullWidth
      component={props => (
        <>
          <Box
            display="flex"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
          >
            <MuiTextField
              {...fieldToTextField(props)}
              variant="filled"
              value={fileName}
              fullWidth
              label={t("addNewContent.addItem.file")}
              InputProps={{
                endAdornment: (
                  <Box mr={2}>
                    <UploadButton
                      acceptType={repositoryFileMimeTypes.join()}
                      buttonVariant="contained"
                      buttonText={t("addNewContent.addItem.chooseFiles")}
                      buttonId="upload-version-file"
                      onUpload={e => {
                        props.form.setFieldValue(
                          newContentFormFields.file,
                          e.target.files && e.target.files[0]
                        );
                        handleFileChange(e.target.files);
                        if (
                          linkedField &&
                          e.target.files &&
                          e.target.files[0]
                        ) {
                          props.form.setFieldValue(
                            linkedField,
                            e.target.files[0].name
                          );
                        }
                      }}
                      multiple={multiple}
                    />
                  </Box>
                ),
                readOnly: true,
                classes: { input: classes.chooseFilesField }
              }}
            />
            <Tooltip title={previewMsg(props.form.values.file)}>
              <Box ml={{ xs: 0, md: 1 }} mt={{ xs: 2, md: 0 }}>
                <Button
                  disabled={
                    !previewableFileTypes.includes(
                      getFileType(props.form.values.file)
                    )
                  }
                  color="primary"
                  onClick={() => setPreviewModalOpen(true)}
                  data-qa="preview-repository-item-btn"
                  startIcon={<PlayCircleFilled />}
                >
                  Preview
                </Button>
              </Box>
            </Tooltip>
          </Box>
          <ItemPreviewModal
            itemName={props.form.values.name}
            itemType={getFileType(props.form.values.file)}
            itemMimeType={
              props.form.values.file ? props.form.values.file.type : null
            }
            previewUrl={
              props.form.values.file
                ? URL.createObjectURL(props.form.values.file)
                : null
            }
            open={previewModalOpen}
            onClose={() => setPreviewModalOpen(false)}
          />
        </>
      )}
    />
  );
};

export const LinkField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Field
      label={t("addNewContent.addItem.link")}
      name={newContentFormFields.link}
      variant="filled"
      fullWidth
      component={TextField}
      {...fieldProps}
    />
  );
};

export const NameField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Field
      label={t("addNewContent.addItem.name")}
      name={newContentFormFields.name}
      variant="filled"
      fullWidth
      component={TextField}
      {...fieldProps}
    />
  );
};

export const DescriptionField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Field
      variant="filled"
      fullWidth
      multiline
      rowsMax={4}
      label={t("addNewContent.addItem.description")}
      component={TextField}
      name={newContentFormFields.description}
      {...fieldProps}
    />
  );
};

export const CommentsField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Field
      variant="filled"
      fullWidth
      multiline
      rowsMax={4}
      component={TextField}
      label={t("addNewContent.addItem.comments")}
      name={newContentFormFields.comment}
      {...fieldProps}
    />
  );
};

export const TagsField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Field
      variant="filled"
      fullWidth
      label={t("addNewContent.addItem.tags")}
      name={newContentFormFields.tags}
      component={ChipInput}
      {...fieldProps}
    />
  );
};

export const DurationFieldHours = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Field
      variant="filled"
      fullWidth
      label={t("addNewContent.addItem.durationHours")}
      component={TextField}
      name={newContentFormFields.durationHours}
      {...fieldProps}
    />
  );
};

export const DurationFieldMinutes = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Select
      name={newContentFormFields.durationMinutes}
      menuItems={durationMenuItems}
      label={t("addNewContent.addItem.durationMinutes")}
    />
  );
};

export const HtmlField = ({ ...fieldProps }) => {
  const [currentTemplate, setCurrentTemplate] = useState("");
  const [content, setContent] = useState(fieldProps.values.content);

  return (
    <NewPageField
      currentTemplate={currentTemplate}
      setCurrentTemplate={setCurrentTemplate}
      setFieldValue={fieldProps.setFieldValue}
      setFilesList={fieldProps.setFilesList}
      filesList={fieldProps.filesList}
      isSubmitting={fieldProps.isSubmitting}
      content={content}
      onContentChange={setContent}
    />
  );
};

export const CourseTitleField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Field
      variant="filled"
      fullWidth
      label={t("addNewContent.addItem.courseTitle")}
      name={newContentFormFields.courseTitle}
      component={TextField}
      {...fieldProps}
    />
  );
};
export const IsQuizImportField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <>
      <Field
        Label={{
          label: (
            <Typography variant="body2">
              {t("addNewContent.addItem.isQuizImport")}
            </Typography>
          )
        }}
        name={newContentFormFields.isQuizImport}
        component={CheckboxWithLabel}
        type="checkbox"
        {...fieldProps}
      />
      <ErrorMessage name={newContentFormFields.isQuizImport}>
        {msg => <Typography className={classes.errorMsg}>{msg}</Typography>}
      </ErrorMessage>
    </>
  );
};

export const QuizOrSurveyField = () => {
  const { t } = useTranslation();
  return (
    <FormControl fullWidth>
      <Field
        type="text"
        variant="filled"
        component={RadioGroup}
        name={newContentFormFields.quizOrSurvey}
        row
      >
        <FormControlLabel
          value="quiz"
          control={<Radio color="primary" data-qa="quiz-or-survey-quiz" />}
          label={t("addNewContent.addItem.quizOrSurvey.quiz")}
        />
        <FormControlLabel
          value="survey"
          control={<Radio color="primary" data-qa="quiz-or-survey-survey" />}
          label={t("addNewContent.addItem.quizOrSurvey.survey")}
        />
      </Field>
    </FormControl>
  );
};

export const QuizInstructionsField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  const [instructions, setInstructions] = useState(
    fieldProps.values.instructions
  );
  return (
    <>
      <Box mb={-2}>
        <Typography variant="subtitle1" color="textSecondary">
          {t("courseBuilder.createQuizInstructionsLabel")}
        </Typography>
      </Box>
      <HTMLEditor
        height={189}
        menubar={false}
        plugins={["lists", "link", "image", "media", "code"]}
        toolbar="undo redo | bold italic underline | alignment numlist bullist outdent indent | link"
        onChange={value => {
          fieldProps.setFieldValue(
            newContentFormFields.quizInstructions,
            value
          );
          setInstructions(value);
        }}
        value={instructions}
      />
    </>
  );
};

export const RandomlyOrderedQuestionsField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  return (
    <Field
      component={CheckboxWithLabel}
      name={newContentFormFields.randomlyOrdered}
      inputProps={{
        "data-qa": "repository-create-quiz-randomize-questions-field"
      }}
      Label={{
        label: (
          <Typography variant="subtitle1">
            {t(`courseBuilder.createQuizRandomlyOrdered.${fieldProps.type}`)}
          </Typography>
        )
      }}
      color="primary"
    />
  );
};

export const NumberOfServedQuestionsField = ({ ...fieldProps }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  return (
    <Box display="flex" flexDirection="column">
      <Typography variant="body2">
        {t("courseBuilder.createQuizNumQuestionsLabel")}
      </Typography>
      <Box mt={2}>
        <Field
          hiddenLabel
          variant="filled"
          component={TextField}
          name={newContentFormFields.numQuestions}
          disabled={fieldProps.disabled}
          type="number"
          InputProps={{
            classes: { root: classes.numQuestionsField },
            inputProps: {
              min: "1",
              "data-qa": "repository-create-quiz-num-questions-field"
            }
          }}
        />
      </Box>
    </Box>
  );
};

export const SurveyAnonymousReportingField = () => {
  const { t } = useTranslation();
  return (
    <Box mt={2}>
      <Field
        component={CheckboxWithLabel}
        name={newContentFormFields.anonymousReporting}
        Label={{
          label: (
            <Typography variant="subtitle1">
              {t("courseBuilder.createQuizAnonymousReporting")}
            </Typography>
          )
        }}
        color="primary"
      />
    </Box>
  );
};

export const QuestionsPerPageField = () => {
  const { t } = useTranslation();
  const questionPerPageOptions = [
    {
      value: "all",
      label: t("courseBuilder.createQuizQuestionsPerPageAllOption")
    },
    { value: "1", label: "1" },
    { value: "5", label: "5" },
    { value: "10", label: "10" }
  ];
  return (
    <Select
      name={newContentFormFields.questionsPerPage}
      menuItems={questionPerPageOptions}
      label={t("courseBuilder.createQuizQuestionsPerPageLabel")}
    />
  );
};

export const EditorBehaviorField = () => {
  const { t } = useTranslation();
  const [disabled, setDisabled] = useState(false);
  const { values, isSubmitting, setFieldValue } = useFormikContext();

  useEffect(() => {
    const fileType = values.file?.type;
    if (
      (fileType &&
        !(
          imageFileMimeTypes.includes(fileType) ||
          HTML5SupportedVideoMimeTypes.includes(fileType)
        )) ||
      !!values.url
    ) {
      // Prevents user from being able to select "Embed" for file types other than videos and images
      setDisabled(true);
      setFieldValue(
        newContentFormFields.editorBehavior,
        editorInlineItemsBehavior.openInNewTab
      );
    } else {
      setDisabled(false);
    }
  }, [values.file, values.url]);

  return (
    <Tooltip
      title={disabled ? t("addNewContent.addItem.embedWarningMessage") : ""}
    >
      <FormControl>
        <Field
          type="text"
          variant="filled"
          component={RadioGroup}
          name={newContentFormFields.editorBehavior}
          row
        >
          <FormControlLabel
            value={editorInlineItemsBehavior.embed}
            control={
              <Radio
                color="primary"
                data-qa="quiz-or-survey-quiz"
                disabled={isSubmitting || disabled}
              />
            }
            label={t("addNewContent.addItem.embed")}
          />
          <FormControlLabel
            value={editorInlineItemsBehavior.openInNewTab}
            control={
              <Radio
                color="primary"
                data-qa="quiz-or-survey-survey"
                disabled={isSubmitting || disabled}
              />
            }
            label={t("addNewContent.addItem.openInNewTab")}
          />
        </Field>
      </FormControl>
    </Tooltip>
  );
};
